import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { capture } from '../../analytics/posthog';
import { AuthContext } from 'components/lib';

const ActivationContext = createContext(null);

export function ActivationProvider({ children }) {
  const [activationData, setActivationData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const authContext = useContext(AuthContext);

  const isHiddenForUser =
    authContext?.user?.permission === 'client' ||
    location?.pathname?.includes('/verification') ||
    location?.pathname?.includes('/magic') ||
    location?.pathname?.includes('/signin') ||
    location?.pathname?.includes('/signup') ||
    location?.pathname?.includes('/onboarding');

  const fetchActivation = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/onboarding/activation');
      if (res?.data?.success && res?.data?.data?.eligible) {
        const data = res.data.data;
        setActivationData(data);

        // Auto-open once for a user who just completed signup onboarding
        if (
          !data.completed_seen &&
          !data.widget_dismissed &&
          localStorage.getItem('activation_widget_auto_open')
        ) {
          localStorage.removeItem('activation_widget_auto_open');
          capture('activation_widget_viewed', { surface: 'auto_open' });
          setIsOpen(true);
        }
      } else {
        setActivationData(null);
      }
    } catch {
      setActivationData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    if (isHiddenForUser) return;
    fetchActivation();
  }, [fetchActivation, isHiddenForUser]);

  // Refetch when entering relevant routes where activation steps occur
  useEffect(() => {
    if (isHiddenForUser) return;
    const path = location.pathname;
    if (
      path.startsWith('/outreach') ||
      path.startsWith('/import-list') ||
      path.startsWith('/leads') ||
      path.startsWith('/account/integration') ||
      path === '/'
    ) {
      fetchActivation();
    }
  }, [location.pathname, fetchActivation, isHiddenForUser]);

  const toggleOpen = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        capture('activation_widget_viewed', { surface: 'floating_widget' });
      }
      return next;
    });
  };

  const close = () => setIsOpen(false);

  const dismissCelebration = async () => {
    try {
      await axios.post('/api/onboarding/activation/dismiss');
      setActivationData((prev) => (prev ? { ...prev, completed_seen: true } : null));
      setIsOpen(false);
    } catch {
      setIsOpen(false);
    }
  };

  const dismissWidget = async () => {
    try {
      await axios.post('/api/onboarding/activation/widget-dismiss');
      setActivationData((prev) => (prev ? { ...prev, widget_dismissed: true } : null));
      setIsOpen(false);
    } catch {
      // Keep the widget visible so the user can retry
    }
  };

  return (
    <ActivationContext.Provider
      value={{
        activationData,
        isOpen,
        loading,
        toggleOpen,
        close,
        fetchActivation,
        dismissCelebration,
        dismissWidget,
      }}
    >
      {children}
    </ActivationContext.Provider>
  );
}

export function useActivation() {
  return useContext(ActivationContext);
}
